import type { FetchStatus } from '@tanstack/react-query';

export interface GuardQueryState {
  isPending: boolean;
  fetchStatus: FetchStatus;
}

/**
 * A disabled TanStack query (`enabled: false`, e.g. before the tenant is
 * validated) reports `isPending: true` forever — it never fetches, so it
 * never settles into `isPending: false`. Reading `isPending` alone in a
 * route guard renders a blank page permanently instead of falling through
 * to the redirect.
 *
 * `fetchStatus` disambiguates: `'fetching'`/`'paused'` means the query is
 * actually in flight (still settling, render nothing yet); `'idle'` with
 * `isPending: true` means the query is disabled and will never resolve on
 * its own — no tenant validated, no data ever coming — so the guard must
 * fall through to its normal decision (redirect, as if there is no data).
 *
 * Allow-list on purpose (N3): a caller or mock that forgets `fetchStatus`
 * must fall through to the redirect, never hang on a blank page.
 */
export function isGuardQuerySettling({ isPending, fetchStatus }: GuardQueryState): boolean {
  return isPending && (fetchStatus === 'fetching' || fetchStatus === 'paused');
}
